import { MutableRefObject } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TimeEntryCategory, User } from "@myapp/contract";
import { FormSection } from "../../../../shared/ui/controls/FormSection/FormSection";
import { FieldWrapper } from "../../../../shared/ui";
import { FormInput } from "../../../../shared/ui/controls/FormInput";
import { EntityMultiSelect } from "../../../../shared/ui/search/EntityMultiSelect/EntityMultiSelect";
import { useUsersInfiniteQuery } from "../../../../entities/user";
import {
  isFullDayCategory,
  timeManagementFormSchema,
  getDefaultValues,
  type TimeManagementFormValues,
} from "../model/timeManagementForm.schema";

export type { TimeManagementFormValues };

interface Props {
  category: TimeEntryCategory;
  onSubmit: (values: TimeManagementFormValues) => void;
  isLoading: boolean;
  submitRef: MutableRefObject<(() => void) | null>;
  initialValues?: TimeManagementFormValues;
  hideTechnicianSelect?: boolean;
  readOnly?: boolean;
}

export function TimeManagementFormWidget({
  category,
  onSubmit,
  isLoading,
  submitRef,
  initialValues,
  hideTechnicianSelect = false,
  readOnly = false,
}: Props) {
  const isFullDay = isFullDayCategory(category);

  const { control, handleSubmit } = useForm<TimeManagementFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(timeManagementFormSchema) as any,
    defaultValues: initialValues ?? getDefaultValues(category),
  });

  submitRef.current = handleSubmit(
    (data) => {
      if (!isLoading) onSubmit(data);
    },
    (errors) => {
      console.error("[TimeManagementForm] validation errors:", errors);
    },
  );

  return (
    <form
      className="flex flex-col gap-5 sm:flex-row sm:gap-10"
      onSubmit={(e) => e.preventDefault()}
    >
      {!hideTechnicianSelect && (
        <FormSection title="Handwerker">
          <Controller
            name="technicianIds"
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <EntityMultiSelect<User & { id: number }>
                  value={field.value}
                  onChange={field.onChange}
                  useInfiniteHook={useUsersInfiniteQuery}
                  searchParamName="search"
                  query={{ role: "WORKER" }}
                  getLabel={(item) => item.name}
                />
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </FormSection>
      )}

      <FormSection title={hideTechnicianSelect ? undefined : "Details"}>
        {isFullDay ? (
          <div className="flex flex-row justify-between gap-5">
            <FieldWrapper label="Von">
              <FormInput name="dateFrom" control={control} type="date" as="input" readOnly={readOnly} className="min-h-[40px]"/>
            </FieldWrapper>
            <FieldWrapper label="Bis">
              <FormInput name="dateTo" control={control} type="date" as="input" readOnly={readOnly} className="min-h-[40px]"/>
            </FieldWrapper>
          </div>
        ) : (
          <>
            <FieldWrapper label="Datum">
              <FormInput name="date" control={control} type="date" as="input" readOnly={readOnly} />
            </FieldWrapper>
            <div className="flex flex-row justify-between gap-5">
              <FieldWrapper label="Von">
                <FormInput name="timeFrom" control={control} type="time" as="input" readOnly={readOnly} />
              </FieldWrapper>
              <FieldWrapper label="Bis">
                <FormInput name="timeTo" control={control} type="time" as="input" readOnly={readOnly} />
              </FieldWrapper>
            </div>
          </>
        )}
        <FieldWrapper label="Kommentar">
          <FormInput name="comment" control={control} as="textarea" rows={3} readOnly={readOnly} />
        </FieldWrapper>
      </FormSection>
    </form>
  );
}
